/**
 * Cliente del servicio público de OSRM para resolver el orden óptimo de visita
 * (problema del viajante). El endpoint `/trip` recibe N coordenadas y devuelve
 * un índice de orden óptimo por waypoint.
 *
 * Estamos usando `router.project-osrm.org` (público, sin auth). Política:
 * "uso razonable", sin SLA. Para tráfico real conviene auto-hostear con
 * la imagen `osrm/osrm-backend` en Docker.
 */

export interface RoutingPoint {
  /** Identificador opaco del caller — el servicio lo devuelve tal cual para mapear. */
  key: string;
  latitude: number;
  longitude: number;
}

export interface OptimizeRouteOptions {
  /** Si true, la ruta vuelve al primer punto (camión que retorna a base). */
  roundtrip?: boolean;
  /** Fija el primer punto como origen. Útil para empezar siempre desde Puente Genil. */
  fixedStart?: boolean;
}

export interface OptimizeRouteResult {
  /** Las keys originales reordenadas según el orden óptimo de visita. */
  orderedKeys: string[];
  /** Distancia total estimada por carretera (km). */
  distanceKm: number;
  /** Duración total estimada (minutos). */
  durationMin: number;
}

const OSRM_BASE = 'https://router.project-osrm.org';

export async function optimizeRoute(
  points: RoutingPoint[],
  options: OptimizeRouteOptions = {},
): Promise<OptimizeRouteResult> {
  if (points.length < 2) {
    throw new Error('Hacen falta al menos 2 puntos para calcular una ruta.');
  }

  // OSRM espera "lon,lat;lon,lat;..."
  const coords = points.map(p => `${p.longitude},${p.latitude}`).join(';');
  const params = new URLSearchParams({
    source: options.fixedStart ? 'first' : 'any',
    destination: options.roundtrip ? 'any' : 'last',
    roundtrip: options.roundtrip ? 'true' : 'false',
    overview: 'false',
  });

  const url = `${OSRM_BASE}/trip/v1/driving/${coords}?${params}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`OSRM devolvió ${response.status}. Inténtalo de nuevo.`);
  }
  const json = await response.json();

  if (json.code !== 'Ok' || !Array.isArray(json.waypoints) || !Array.isArray(json.trips) || json.trips.length === 0) {
    throw new Error(`OSRM no devolvió una ruta válida: ${json.code || 'desconocido'}`);
  }

  // waypoints viene en el orden original con un campo `waypoint_index` que indica
  // su posición en el trip óptimo. Construimos la lista ordenada invirtiéndolo.
  const ordered: string[] = new Array(points.length);
  for (let i = 0; i < json.waypoints.length; i++) {
    const wp = json.waypoints[i];
    const idx = typeof wp.waypoint_index === 'number' ? wp.waypoint_index : i;
    ordered[idx] = points[i].key;
  }

  const trip = json.trips[0];
  return {
    orderedKeys: ordered.filter(Boolean),
    distanceKm: (trip.distance ?? 0) / 1000,
    durationMin: (trip.duration ?? 0) / 60,
  };
}

/**
 * Calcula la distancia/tiempo de una ruta siguiendo el orden EXACTO de los
 * puntos proporcionados (sin reordenar). Sirve para estimar las métricas
 * cuando el usuario ha editado el orden a mano.
 */
export async function computeRouteDistance(
  points: RoutingPoint[],
): Promise<{ distanceKm: number; durationMin: number }> {
  if (points.length < 2) {
    throw new Error('Hacen falta al menos 2 puntos para calcular distancia.');
  }
  const coords = points.map(p => `${p.longitude},${p.latitude}`).join(';');
  const url = `${OSRM_BASE}/route/v1/driving/${coords}?overview=false&alternatives=false&steps=false`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`OSRM devolvió ${response.status}.`);
  }
  const json = await response.json();
  if (json.code !== 'Ok' || !Array.isArray(json.routes) || json.routes.length === 0) {
    throw new Error(`OSRM no devolvió ruta válida: ${json.code || 'desconocido'}`);
  }
  const route = json.routes[0];
  return {
    distanceKm: (route.distance ?? 0) / 1000,
    durationMin: (route.duration ?? 0) / 60,
  };
}
